import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  type FC,
} from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import type { Contact } from "../../types/deal";
import "../../assets/css/AGGrid-custom.css";

interface DealContactsTableProps {
  dealId: number | undefined;
  contactsRef: React.RefObject<HTMLDivElement | null>;
}

const DealContactsTable: FC<DealContactsTableProps> = ({
  dealId,
  contactsRef,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const contactsGridRef = useRef<AgGridReact<Contact>>(null);
  const { sponsors, deals, sources } = useReferenceLists();

  // Fetch contacts for the deal using the backend endpoint
  // This endpoint handles all the logic for finding contacts linked to:
  // deal, primary sponsor, secondary sponsor, and source
  useEffect(() => {
    if (!dealId) {
      setContacts([]);
      return;
    }

    let isMounted = true;

    apiGetById("contacts/by-deal", dealId)
      .then((response) => {
        if (!isMounted) return;

        if (response && response.success && response.data) {
          // Response data should already be an array of contacts, deduplicated by the backend
          setContacts(Array.isArray(response.data) ? response.data : []);
        } else {
          setContacts([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching contacts for deal:", err);
        if (isMounted) {
          setContacts([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dealId]);

  // Helper function to get sponsor IDs from links (including implied from entities)
  const getSponsorIdsFromLinks = useCallback(
    (links: any[] | undefined): number[] => {
      if (!links?.length) return [];

      const directSponsorIds = links
        .map((l) => l?.sponsor_id)
        .filter((v): v is number => typeof v === "number" && v > 0);

      return Array.from(new Set([...directSponsorIds]));
    },
    [],
  );

  // Cell renderer for sponsors with links
  const sponsorLinksRenderer = useCallback(
    (params: ICellRendererParams<Contact>) => {
      const links = params.data?.links;
      if (!links?.length) return "-";

      const sponsorIds = getSponsorIdsFromLinks(links);
      if (!sponsorIds.length) return "-";

      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {sponsorIds.map((id, idx) => {
            const sponsor = sponsors?.find((s: any) => s?.id === id);
            const name = sponsor?.sponsor_name || `ID ${id}`;
            return (
              <span key={id}>
                {idx > 0 && ", "}
                <a
                  href={`/sponsors/${id}`}
                  style={{ color: "#1B7A63", textDecoration: "underline" }}
                >
                  {name}
                </a>
              </span>
            );
          })}
        </Box>
      );
    },
    [sponsors, getSponsorIdsFromLinks],
  );

  // Cell renderer for entities with links

  // Cell renderer for deals with links
  const dealLinksRenderer = useCallback(
    (params: ICellRendererParams<Contact>) => {
      const links = params.data?.links;
      if (!links?.length) return "-";

      const dealIds = links
        .map((l) => l?.deal_id)
        .filter((v): v is number => typeof v === "number" && v > 0);

      const uniqueDealIds = Array.from(new Set(dealIds));
      if (!uniqueDealIds.length) return "-";

      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {uniqueDealIds.map((id, idx) => {
            const deal = deals?.find((d: any) => d?.id === id);
            const name = deal?.deal_name || `ID ${id}`;
            return (
              <span key={id}>
                {idx > 0 && ", "}
                <a
                  href={`/deals/${id}`}
                  style={{ color: "#1B7A63", textDecoration: "underline" }}
                >
                  {name}
                </a>
              </span>
            );
          })}
        </Box>
      );
    },
    [deals],
  );

  // Cell renderer for sources with links
  const sourceLinksRenderer = useCallback(
    (params: ICellRendererParams<Contact>) => {
      const links = params.data?.links;
      if (!links?.length) return "-";

      const sourceIds = links
        .map((l) => l?.source_id)
        .filter((v): v is number => typeof v === "number" && v > 0);

      const uniqueSourceIds = Array.from(new Set(sourceIds));
      if (!uniqueSourceIds.length) return "-";

      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {uniqueSourceIds.map((id, idx) => {
            const source = sources?.find((s: any) => s?.id === id);
            const name = source?.source_name || `ID ${id}`;
            return (
              <span key={id}>
                {idx > 0 && ", "}
                <a
                  href={`/sources/${id}`}
                  style={{ color: "#1B7A63", textDecoration: "underline" }}
                >
                  {name}
                </a>
              </span>
            );
          })}
        </Box>
      );
    },
    [sources],
  );

  // Cell renderer for email with mailto link
  const emailRenderer = useCallback((params: ICellRendererParams<Contact>) => {
    const email = params.value;
    if (!email || typeof email !== "string" || !email.trim()) {
      return "-";
    }

    return (
      <a
        href={`mailto:${email.trim()}`}
        style={{ color: "#1B7A63", textDecoration: "underline" }}
      >
        {email}
      </a>
    );
  }, []);

  // Column definitions for contacts table
  const contactsColumnDefs: ColDef<Contact>[] = useMemo(
    () => [
      {
        field: "contact_name",
        headerName: "Contact",
        width: 180,
        cellRenderer: HyperLinkRenderer("contacts"),
      },
      { field: "company", headerName: "Company", width: 150 },
      {
        field: "contact_email",
        headerName: "Email",
        width: 200,
        cellRenderer: emailRenderer,
      },
      { field: "contact_title", headerName: "Job Title", width: 150 },
      { field: "contact_phone", headerName: "Phone", width: 130 },
      {
        headerName: "Sponsors",
        colId: "link_sponsors",
        width: 180,
        cellRenderer: sponsorLinksRenderer,
      },
      {
        headerName: "Deals",
        colId: "link_deals",
        width: 180,
        cellRenderer: dealLinksRenderer,
      },
      {
        headerName: "Sources",
        colId: "link_sources",
        width: 180,
        cellRenderer: sourceLinksRenderer,
      },
      { field: "contact_location", headerName: "Location", width: 150 },
    ],
    [
      sponsorLinksRenderer,
      dealLinksRenderer,
      sourceLinksRenderer,
      emailRenderer,
    ],
  );

  return (
    <Box ref={contactsRef} sx={{ mt: 0, scrollMarginTop: 60 }}>
      <Box sx={{ height: 400, width: "100%", mt: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact<Contact>
            ref={contactsGridRef}
            rowData={contacts}
            columnDefs={contactsColumnDefs}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
            rowHeight={35}
            getRowId={({ data }) => data?.id?.toString() ?? ""}
            overlayNoRowsTemplate="No contacts found linked to this deal, its sponsors, entities, or source."
          />
        </div>
      </Box>
    </Box>
  );
};

export default DealContactsTable;
